import React from 'react';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { toast } from 'react-toastify';

/**
 * The food items tariff, on both rate screens.
 *
 * Without `clientEmail` this is the standard tariff every cash customer gets. With one
 * it is that credit customer's negotiated version — and the standard rows they inherit
 * are shown alongside, greyed, so it is obvious which prices are actually theirs and
 * which are simply the default.
 *
 * Priced by destination and commodity rather than by zone, so it does not fit the
 * shared CreditRateTable and has its own columns.
 */

type Props = {
    /** Omit for the standard tariff; pass an email for a credit customer's own rates. */
    clientEmail?: string;
};

const money = (v: any) =>
    `₦${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const apiError = (e: any, fallback: string) =>
    e?.response?.data?.message || e?.message || fallback;

const COMMODITY_LABELS: Record<string, string> = {
    food: 'Food',
    fish: 'Fish',
    cosmetics: 'Cosmetics & Cream',
};

function FoodRateTable({ clientEmail }: Props) {
    const [rates, setRates] = React.useState<any[]>([]);
    const [charges, setCharges] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [busy, setBusy] = React.useState<string>('');

    const [editingId, setEditingId] = React.useState<any>(null);
    const [draft, setDraft] = React.useState<any>({});

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const res: any = await SuperAdminApis.getFoodRates(clientEmail);
            if (res?.data?.status) {
                setRates(res.data.data.rates || []);
                setCharges(res.data.data.document_charges || []);
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not load the food tariff'));
        } finally {
            setLoading(false);
        }
    }, [clientEmail]);

    React.useEffect(() => { load(); }, [load]);

    const startEdit = (row: any) => {
        setEditingId(row.id);
        setDraft({
            first_kg_rate: row.first_kg_rate ?? '',
            additional_kg_rate: row.additional_kg_rate ?? '',
            bulk_from_kg: row.bulk_from_kg ?? '',
            bulk_rate_per_kg: row.bulk_rate_per_kg ?? '',
        });
    };

    const saveEdit = async (row: any) => {
        setBusy(`save-${row.id}`);
        try {
            const res: any = await SuperAdminApis.updateFoodRate(row.id, {
                ...draft,
                bulk_from_kg: draft.bulk_from_kg === '' ? null : draft.bulk_from_kg,
                bulk_rate_per_kg: draft.bulk_rate_per_kg === '' ? null : draft.bulk_rate_per_kg,
            });
            if (res?.data?.status) {
                toast.success(res.data.message);
                setEditingId(null);
                load();
            } else {
                toast.warn(res?.data?.message || 'Could not update this rate');
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not update this rate'));
        } finally {
            setBusy('');
        }
    };

    const removeRow = async (row: any) => {
        const what = `${COMMODITY_LABELS[row.commodity] || row.commodity} to ${row.destination_label}`;

        if (!window.confirm(
            `Delete ${what}?\n\n` +
            `It can no longer be booked${row.is_standard ? ' by any cash customer' : ` for ${clientEmail}`}.`
        )) return;

        setBusy(`del-${row.id}`);
        try {
            const res: any = await SuperAdminApis.deleteFoodRate(row.id);
            if (res?.data?.status) { toast.success(res.data.message); load(); }
            else toast.warn(res?.data?.message || 'Could not delete this rate');
        } catch (e) {
            toast.error(apiError(e, 'Could not delete this rate'));
        } finally {
            setBusy('');
        }
    };

    const download = async (which: 'template' | 'export') => {
        setBusy(which);
        try {
            const res: any = which === 'template'
                ? await SuperAdminApis.downloadFoodRateTemplate()
                : await SuperAdminApis.exportFoodRates(clientEmail);

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = which === 'template'
                ? 'food-rates-template.csv'
                : `food-rates-${clientEmail || 'standard'}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(apiError(e, 'Download failed'));
        } finally {
            setBusy('');
        }
    };

    const upload = async (e: any) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const form = new FormData();
        form.append('file', file);
        if (clientEmail) form.append('client_id', clientEmail);

        setBusy('upload');
        try {
            const res: any = await SuperAdminApis.uploadFoodRates(form);
            if (res?.data?.status) { toast.success(res.data.message); load(); }
            else toast.warn(res?.data?.message || 'Upload failed');
        } catch (err: any) {
            // The backend validates the whole file before writing any of it, and
            // returns the offending lines. Showing them beats "upload failed".
            const lines = err?.response?.data?.errors;
            if (Array.isArray(lines) && lines.length) {
                toast.error(err?.response?.data?.message || 'Upload rejected');
                lines.slice(0, 5).forEach((l: string) => toast.warn(l));
            } else {
                toast.error(apiError(err, 'Upload failed'));
            }
        } finally {
            setBusy('');
            e.target.value = '';
        }
    };

    // Only rows this screen owns can be cleared: viewing a customer shows the standard
    // rows they inherit, and Delete All there must not wipe the tariff for everyone.
    const ownRows = clientEmail ? rates.filter((r: any) => !r.is_standard) : rates;

    const deleteAll = async () => {
        const answer = window.prompt(
            `This deletes ALL ${ownRows.length} food rate(s) for ${clientEmail || 'the standard tariff'}.\n\n` +
            `Type ${ownRows.length} to confirm.`
        );

        if (answer === null) return;
        if (answer.trim() !== String(ownRows.length)) { toast.info('Cancelled — the number did not match.'); return; }

        setBusy('delete-all');
        try {
            const res: any = await SuperAdminApis.deleteAllFoodRates(clientEmail);
            if (res?.data?.status) { toast.success(res.data.message); load(); }
            else toast.warn(res?.data?.message || 'Could not clear the tariff');
        } catch (e) {
            toast.error(apiError(e, 'Could not clear the tariff'));
        } finally {
            setBusy('');
        }
    };

    return (
        <div>
            <div className='flex flex-wrap justify-between items-center gap-3 mb-2'>
                <div>
                    <h3 className='text-[16px] text-[#1C1C1C] font-semibold'>Food Rates</h3>
                    <p className='text-[11px] text-gray-500'>
                        {clientEmail
                            ? `${clientEmail}'s negotiated food prices. Rows marked "standard" are inherited and apply until you set your own.`
                            : 'The standard food tariff, used for cash customers and any credit customer without their own.'}
                    </p>
                </div>

                <div className='flex flex-wrap gap-2'>
                    <button onClick={() => download('template')} disabled={!!busy}
                        className="bg-blue-500 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-600 disabled:opacity-40">
                        Download Template
                    </button>
                    <button onClick={() => download('export')} disabled={!ownRows.length || !!busy}
                        className="bg-gray-700 text-white px-4 py-2 rounded-md text-sm hover:bg-gray-800 disabled:opacity-40">
                        {busy === 'export' ? 'Exporting…' : 'Export Current'}
                    </button>
                    <label className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm cursor-pointer hover:bg-[#c43939]">
                        {busy === 'upload' ? 'Uploading…' : 'Upload Excel'}
                        <input type="file" accept=".csv,.xlsx,.xls" onChange={upload} className="hidden" disabled={!!busy} />
                    </label>
                    <button onClick={deleteAll} disabled={!ownRows.length || !!busy}
                        className="bg-white border border-[#E04343] text-[#E04343] px-4 py-2 rounded-md text-sm hover:bg-red-50 disabled:opacity-40">
                        {busy === 'delete-all' ? 'Deleting…' : 'Delete All'}
                    </button>
                </div>
            </div>

            <p className='text-[11px] text-gray-500 mb-3'>
                Uploading adds and updates rows but never removes them. A destination and item type with
                no row here cannot be booked at all — that is how "we don't ship cosmetics to the UK" is enforced.
            </p>

            <div className="relative overflow-x-auto bg-white rounded-lg">
                <table className="w-full text-sm text-left">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        <tr>
                            <th className="px-4 py-3">Destination</th>
                            <th className="px-4 py-3">Item Type</th>
                            <th className="px-4 py-3">First 1kg</th>
                            <th className="px-4 py-3">Additional /kg</th>
                            <th className="px-4 py-3">Bulk from</th>
                            <th className="px-4 py-3">Bulk /kg</th>
                            <th className="px-4 py-3 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-500">Loading…</td></tr>
                        ) : rates.length === 0 ? (
                            <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                                No food rates yet. Download the template, fill it in, and upload it.
                            </td></tr>
                        ) : rates.map((r: any) => {
                            const editing = editingId === r.id;
                            // A standard row shown on a customer's screen is not theirs to edit here.
                            const inherited = !!clientEmail && r.is_standard;

                            return (
                                <tr key={r.id} className={`bg-white border-b ${inherited ? 'opacity-60' : 'hover:bg-gray-50'}`}>
                                    <td className="px-4 py-3 font-medium">
                                        {r.destination_label}
                                        {inherited && <span className='ml-2 text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded'>standard</span>}
                                    </td>
                                    <td className="px-4 py-3">{COMMODITY_LABELS[r.commodity] || r.commodity}</td>

                                    {['first_kg_rate', 'additional_kg_rate', 'bulk_from_kg', 'bulk_rate_per_kg'].map((key) => (
                                        <td key={key} className="px-4 py-3">
                                            {editing ? (
                                                <input type="number" step="0.01" min="0"
                                                    value={draft[key] ?? ''}
                                                    onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                                                    placeholder={key.startsWith('bulk') ? 'none' : ''}
                                                    className="border rounded px-2 py-1 w-24 text-sm" />
                                            ) : r[key] === null || r[key] === undefined ? (
                                                <span className='text-gray-400'>—</span>
                                            ) : key === 'bulk_from_kg' ? (
                                                `${Number(r[key])}kg`
                                            ) : (
                                                money(r[key])
                                            )}
                                        </td>
                                    ))}

                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        {inherited ? (
                                            <span className='text-gray-400 text-xs'>inherited</span>
                                        ) : editing ? (
                                            <>
                                                <button onClick={() => saveEdit(r)} disabled={!!busy}
                                                    className="text-green-700 font-medium hover:underline disabled:opacity-40">
                                                    {busy === `save-${r.id}` ? 'Saving…' : 'Save'}
                                                </button>
                                                <button onClick={() => setEditingId(null)} className="ml-3 text-gray-500 hover:underline">Cancel</button>
                                            </>
                                        ) : (
                                            <>
                                                <button onClick={() => startEdit(r)} disabled={!!busy}
                                                    className="text-blue-600 font-medium hover:underline disabled:opacity-40">Edit</button>
                                                <button onClick={() => removeRow(r)} disabled={!!busy}
                                                    className="ml-3 text-[#E04343] font-medium hover:underline disabled:opacity-40">
                                                    {busy === `del-${r.id}` ? 'Deleting…' : 'Delete'}
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* The phytosanitary rule lives in data so the threshold can move without a
                deploy. Shown read-only here — it is set on the standard tariff screen. */}
            {charges.length > 0 && (
                <div className='mt-5 bg-white rounded-lg p-4'>
                    <h4 className='text-sm font-semibold mb-2'>Documentation charges</h4>
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                            <tr>
                                <th className="px-4 py-2">Region</th>
                                <th className="px-4 py-2">Document</th>
                                <th className="px-4 py-2">Amount</th>
                                <th className="px-4 py-2">Charged from</th>
                                <th className="px-4 py-2">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {charges.map((c: any) => (
                                <tr key={c.id} className="border-b last:border-0">
                                    <td className="px-4 py-2">{c.region === 'UK' ? 'United Kingdom' : 'USA & Canada'}</td>
                                    <td className="px-4 py-2">{c.name}</td>
                                    <td className="px-4 py-2">{money(c.amount)}</td>
                                    <td className="px-4 py-2">
                                        {Number(c.min_weight_kg) > 0 ? `${Number(c.min_weight_kg)}kg and above` : 'Every shipment'}
                                    </td>
                                    <td className="px-4 py-2">
                                        {c.is_active
                                            ? <span className='text-green-700 text-xs'>Active</span>
                                            : <span className='text-gray-400 text-xs'>Off</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default FoodRateTable;
